/*
 * Super Admin — Annual License / Subscription Management (Phase 3).
 *
 * Mounted at /app/licenses. Two-pane layout:
 *   • Left: filter chips (All / Active / Renewal Due / Grace / Expired / Cancelled)
 *           + searchable license table
 *   • Right: detail card with end-date editor, manual renew/cancel/mark-paid,
 *           PO approval, "Send reminder now" button, and reminder cadence summary.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import {
  CalendarDays, RefreshCcw, AlertTriangle, CheckCircle2, X,
  CreditCard, Mail, Search, Download, FileText,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const adminHdrs = (session) => ({
  "x-tenant-id": session?.tenant?.id || "tenant_1",
  "x-user-id":   session?.user?.id   || "",
  "x-user-role": session?.user?.role || "admin",
});

// Mapping: effective_status → {label, classes}
const STATUS_PILL = {
  active:              { label: "Active",             cls: "text-[#065f46] bg-[#ecfdf5] border-[#a7f3d0]" },
  manually_activated:  { label: "Manually Activated", cls: "text-[#065f46] bg-[#ecfdf5] border-[#a7f3d0]" },
  renewal_due:         { label: "Renewal Due",        cls: "text-[#00607d] bg-[#f0f9fc] border-[#bfe3ee]" },
  past_due:            { label: "Past Due",           cls: "text-[#b91c1c] bg-[#fef2f2] border-[#fecaca]" },
  grace_period:        { label: "Grace Period",       cls: "text-[#a16207] bg-[#fefce8] border-[#fde047]" },
  expired:             { label: "Expired",            cls: "text-[#b91c1c] bg-[#fef2f2] border-[#fecaca]" },
  cancelled:           { label: "Cancelled",          cls: "text-[#6b7280] bg-[#f3f4f6] border-[#e5e7eb]" },
  pending_payment:     { label: "Pending Payment",    cls: "text-[#1e40af] bg-[#eff6ff] border-[#bfdbfe]" },
};

const FILTERS = [
  { id: "all",         label: "All" },
  { id: "active",      label: "Active" },
  { id: "manually_activated", label: "Manual" },
  { id: "renewal_due", label: "Renewal Due" },
  { id: "grace_period", label: "Grace" },
  { id: "expired",     label: "Expired" },
  { id: "cancelled",   label: "Cancelled" },
];

const LicensesAdminPage = ({ session, presetProduct }) => {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [productFilter, setProductFilter] = useState(presetProduct || "caporilli_diagnostic");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const url = productFilter === "all"
        ? `${API}/api/admin/licenses`
        : `${API}/api/admin/licenses?product_type=${productFilter}`;
      const r = await axios.get(url, { headers: adminHdrs(session) });
      setLicenses(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load licenses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [productFilter]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return licenses.filter((l) => {
      if (filter !== "all" && l.effective_status !== filter) return false;
      if (s && !((l.tenant_name || "").toLowerCase().includes(s) || (l.tenant_id || "").toLowerCase().includes(s))) return false;
      return true;
    });
  }, [licenses, filter, search]);

  const counts = useMemo(() => {
    const out = {};
    for (const id of FILTERS.map((f) => f.id)) out[id] = 0;
    out.all = licenses.length;
    for (const l of licenses) {
      if (out[l.effective_status] !== undefined) out[l.effective_status]++;
    }
    return out;
  }, [licenses]);

  const updateLicense = async (id, patch) => {
    try {
      const r = await axios.patch(`${API}/api/admin/licenses/${id}`, patch, { headers: adminHdrs(session) });
      setLicenses((arr) => arr.map((l) => (l.id === id ? { ...l, ...r.data, effective_status: l.effective_status } : l)));
      await load();
      const updated = await axios.get(
        productFilter === "all" ? `${API}/api/admin/licenses` : `${API}/api/admin/licenses?product_type=${productFilter}`,
        { headers: adminHdrs(session) }
      );
      const nu = updated.data.find((l) => l.id === id);
      if (nu) setSelected(nu);
      toast.success("License updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const renew = async (id, months = 12) => {
    if (!window.confirm(`Renew this license for ${months} months and mark as paid?`)) return;
    try {
      await axios.post(`${API}/api/admin/licenses/${id}/renew`, { months, mark_paid: true }, { headers: adminHdrs(session) });
      toast.success(`Renewed +${months} months`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Renew failed");
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this license? Customer will lose report access immediately.")) return;
    try {
      await axios.post(`${API}/api/admin/licenses/${id}/cancel`, {}, { headers: adminHdrs(session) });
      toast.success("License cancelled");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  const markPaid = async (id) => {
    try {
      await axios.post(`${API}/api/admin/licenses/${id}/mark-paid`, {}, { headers: adminHdrs(session) });
      toast.success("Marked paid");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const sendReminder = async (id) => {
    try {
      await axios.post(`${API}/api/admin/licenses/${id}/send-reminder`, {}, { headers: adminHdrs(session) });
      toast.success("Reminder queued");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const scanAll = async () => {
    try {
      const r = await axios.post(`${API}/api/admin/licenses/scan-reminders`, {}, { headers: adminHdrs(session) });
      toast.success(`Scan complete · ${r.data.sent} reminder(s) queued`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const toggleAICorrectivePlan = async (id, enabled) => {
    try {
      await axios.post(
        `${API}/api/admin/licenses/${id}/ai-corrective-plan`,
        { enabled },
        { headers: adminHdrs(session) }
      );
      toast.success(`AI Corrective Action Plan ${enabled ? "enabled" : "disabled"}`);
      await load();
      const updated = await axios.get(
        productFilter === "all" ? `${API}/api/admin/licenses` : `${API}/api/admin/licenses?product_type=${productFilter}`,
        { headers: adminHdrs(session) }
      );
      const nu = updated.data.find((l) => l.id === id);
      if (nu) setSelected(nu);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  return (
    <div className="px-6 lg:px-10 py-8 max-w-[1600px] mx-auto" data-testid="licenses-admin-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[11.5px] uppercase tracking-wider text-[#6b7280] font-semibold">Operations</div>
          <h1 className="text-3xl font-bold text-[#111827] flex items-center gap-2">
            <CalendarDays className="h-7 w-7 text-[#3B4FE4]" /> Licenses · {productFilter === "clarity_full" ? "Clarity Assessments" : productFilter === "all" ? "All Products" : "MySchoolRx"}
          </h1>
          <p className="text-sm text-[#6b7280] mt-1">
            Annual license + renewal management. Reminder cadence: 90 / 60 / 30 / 14 / 7 days before expiration + on-expiry + NJ annual report cycle.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={scanAll} variant="outline" className="border-[#e5e7eb]" data-testid="licenses-scan-btn">
            <Mail className="h-4 w-4 mr-1" /> Run Reminder Scan
          </Button>
          <Button onClick={load} variant="outline" className="border-[#e5e7eb]">
            <RefreshCcw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Product selector */}
      <div className="flex items-center gap-2 mb-3" data-testid="license-product-selector">
        <span className="text-[11.5px] uppercase tracking-wider text-[#6b7280] font-semibold">Product:</span>
        {[
          { id: "caporilli_diagnostic", label: "MySchoolRx" },
          { id: "clarity_full", label: "Clarity Assessments" },
          { id: "all", label: "All Products" },
        ].map((p) => (
          <button
            key={p.id}
            onClick={() => { setProductFilter(p.id); setSelected(null); }}
            className={`px-3 py-1.5 rounded-md text-[12px] font-semibold border transition-colors ${
              productFilter === p.id
                ? "bg-[#3B4FE4] text-white border-[#3B4FE4]"
                : "bg-white text-[#374151] border-[#e5e7eb] hover:bg-[#f9fafb]"
            }`}
            data-testid={`license-product-${p.id}`}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border transition-colors ${
              filter === f.id
                ? "bg-[#0b1220] text-white border-[#0b1220]"
                : "bg-white text-[#374151] border-[#e5e7eb] hover:bg-[#f9fafb]"
            }`}
            data-testid={`license-filter-${f.id}`}
          >
            {f.label} <span className={`ml-1 text-[11.5px] ${filter === f.id ? "text-white/60" : "text-[#9ca3af]"}`}>({counts[f.id] || 0})</span>
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1 border border-[#e5e7eb] rounded-md bg-white px-2">
          <Search className="h-3.5 w-3.5 text-[#9ca3af]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by school name…"
            className="bg-transparent text-[13px] py-1.5 px-1 outline-none w-[240px]"
            data-testid="license-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-[#6b7280]">Loading licenses…</div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_380px] gap-6">
          {/* License table */}
          <div className="rounded-xl border border-[#e5e7eb] bg-white overflow-hidden">
            <table className="w-full text-[13px]" data-testid="licenses-table">
              <thead className="bg-[#f9fafb] text-[11.5px] uppercase text-[#6b7280]">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold">Tenant</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                  <th className="text-left px-4 py-2.5 font-semibold">End Date</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Seats</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Price</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Method</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-[#9ca3af]">No licenses match this filter.</td></tr>
                )}
                {filtered.map((l) => {
                  const pill = STATUS_PILL[l.effective_status] || { label: l.effective_status, cls: "text-[#6b7280] bg-[#f3f4f6] border-[#e5e7eb]" };
                  const isSelected = selected?.id === l.id;
                  return (
                    <tr
                      key={l.id}
                      onClick={() => setSelected(l)}
                      className={`cursor-pointer border-t border-[#e5e7eb] ${isSelected ? "bg-[#eff6ff]" : "hover:bg-[#f9fafb]"}`}
                      data-testid={`license-row-${l.id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="font-semibold text-[#111827]">{l.tenant_name}</div>
                        <div className="text-[11.5px] text-[#9ca3af]">{l.tenant_id}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[11.5px] font-bold uppercase px-2 py-0.5 rounded border ${pill.cls}`}>{pill.label}</span>
                      </td>
                      <td className="px-4 py-3 text-[#374151]">
                        {l.end_date ? new Date(l.end_date).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3 text-[#374151]" data-testid={`license-seats-${l.id}`}>
                        {(l.included_user_seats || 5) + (l.additional_user_seats || 0)}
                        {l.additional_user_seats > 0 && (
                          <span className="text-[11.5px] text-[#6b7280] ml-1">(+{l.additional_user_seats})</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[#374151]">${(l.price || 0).toLocaleString()}/yr</td>
                      <td className="px-4 py-3 text-[#6b7280]">{l.payment_method || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Detail panel */}
          <aside className="lg:sticky lg:top-6 lg:self-start">
            {!selected ? (
              <div className="rounded-xl border border-[#e5e7eb] bg-white p-8 text-center text-[#9ca3af]">
                Select a license to view details and manage renewal.
              </div>
            ) : (
              <LicenseDetailPanel
                license={selected}
                onClose={() => setSelected(null)}
                onUpdate={(patch) => updateLicense(selected.id, patch)}
                onRenew={(months) => renew(selected.id, months)}
                onCancel={() => cancel(selected.id)}
                onMarkPaid={() => markPaid(selected.id)}
                onSendReminder={() => sendReminder(selected.id)}
                onToggleAICorrective={(enabled) => toggleAICorrectivePlan(selected.id, enabled)}
                onUpdateSeats={async (patch) => {
                  try {
                    await axios.patch(`${API}/api/admin/licenses/${selected.id}/seats`, patch, { headers: adminHdrs(session) });
                    toast.success("Seats updated");
                    load();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Seat update failed"); }
                }}
              />
            )}
          </aside>
        </div>
      )}
    </div>
  );
};

const LicenseDetailPanel = ({ license, onClose, onUpdate, onRenew, onCancel, onMarkPaid, onSendReminder, onUpdateSeats, onToggleAICorrective }) => {
  const [editEnd, setEditEnd] = useState((license.end_date || "").substring(0, 10));
  const [editPrice, setEditPrice] = useState(license.price || 4995);
  const [editPayment, setEditPayment] = useState(license.payment_method || "po");
  const [editAutoRenew, setEditAutoRenew] = useState(!!license.auto_renew_enabled);
  const [editNotes, setEditNotes] = useState(license.notes || "");
  const [editAdditionalSeats, setEditAdditionalSeats] = useState(license.additional_user_seats || 0);
  const [seatsComp, setSeatsComp] = useState(false);

  const dirty =
    editEnd !== (license.end_date || "").substring(0, 10) ||
    Number(editPrice) !== Number(license.price || 0) ||
    editPayment !== (license.payment_method || "") ||
    editAutoRenew !== !!license.auto_renew_enabled ||
    editNotes !== (license.notes || "");

  const pill = STATUS_PILL[license.effective_status] || { label: license.effective_status, cls: "text-[#6b7280] bg-[#f3f4f6] border-[#e5e7eb]" };
  const expired = ["expired", "grace_period", "cancelled"].includes(license.effective_status);

  return (
    <div className="rounded-xl border border-[#e5e7eb] bg-white p-5 space-y-4" data-testid="license-detail-panel">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[11.5px] uppercase tracking-wider text-[#6b7280] font-semibold">License</div>
          <h2 className="text-lg font-bold text-[#111827] leading-snug">{license.tenant_name}</h2>
          <div className="text-[11.5px] text-[#9ca3af] mt-0.5">{license.tenant_id}</div>
        </div>
        <button onClick={onClose} className="text-[#9ca3af] hover:text-[#111827]"><X className="h-4 w-4" /></button>
      </div>

      <div className={`rounded-md border px-3 py-2 text-[11.5px] font-semibold inline-flex items-center gap-1.5 ${pill.cls}`}>
        {pill.label}
      </div>

      <div className="text-[12px] text-[#374151] space-y-1">
        <div><strong>Started:</strong> {license.start_date ? new Date(license.start_date).toLocaleDateString() : "—"}</div>
        <div><strong>Renewal due:</strong> {license.renewal_date ? new Date(license.renewal_date).toLocaleDateString() : "—"}</div>
        <div><strong>Grace ends:</strong> {license.grace_period_end_date ? new Date(license.grace_period_end_date).toLocaleDateString() : "—"}</div>
        <div><strong>Last payment:</strong> {license.last_payment_date ? new Date(license.last_payment_date).toLocaleDateString() : "—"}</div>
      </div>

      {expired && (
        <div className="rounded-md border border-[#fecaca] bg-[#fef2f2] px-3 py-2 text-[12px] text-[#b91c1c] flex items-start gap-2">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <div>This tenant has lost report & download access. Renew or extend grace to restore.</div>
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-2">
        <Button onClick={() => onRenew(12)} className="bg-[#059669] hover:bg-[#047857] text-white" size="sm" data-testid="license-renew-12">
          <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Renew 12 mo
        </Button>
        <Button onClick={() => onRenew(1)} variant="outline" className="border-[#e5e7eb]" size="sm" data-testid="license-renew-1">
          Renew +1 mo
        </Button>
        <Button onClick={onMarkPaid} variant="outline" className="border-[#e5e7eb]" size="sm" data-testid="license-mark-paid">
          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark Paid
        </Button>
        <Button onClick={onSendReminder} variant="outline" className="border-[#e5e7eb]" size="sm" data-testid="license-send-reminder">
          <Mail className="h-3.5 w-3.5 mr-1" /> Send Reminder
        </Button>
      </div>

      {/* AI Corrective Action Plan add-on entitlement */}
      <div className="pt-3 border-t border-[#e5e7eb] space-y-2" data-testid="license-ai-plan-section">
        <div className="text-[11.5px] uppercase tracking-wider text-[#6b7280] font-semibold">AI Corrective Action Plan</div>
        <div className="text-[12px] text-[#374151]">
          Status: <b className={license.ai_corrective_action_plan_enabled ? "text-[#065f46]" : "text-[#6b7280]"} data-testid="license-ai-plan-status">
            {license.ai_corrective_action_plan_enabled ? "Active (paid add-on)" : "Locked (not purchased)"}
          </b>
        </div>
        <div className="text-[11.5px] text-[#6b7280]">
          Paid add-on, not included in the base annual license. Toggle to grant or revoke for this tenant.
        </div>
        <Button
          onClick={() => onToggleAICorrective && onToggleAICorrective(!license.ai_corrective_action_plan_enabled)}
          size="sm"
          className={license.ai_corrective_action_plan_enabled
            ? "bg-[#fef2f2] hover:bg-[#fee2e2] text-[#b91c1c] border border-[#fecaca]"
            : "bg-[#059669] hover:bg-[#047857] text-white"}
          data-testid="license-ai-plan-toggle"
        >
          {license.ai_corrective_action_plan_enabled ? "Disable AI Plan" : "Enable AI Plan"}
        </Button>
      </div>

      {/* Seat management */}
      <div className="pt-3 border-t border-[#e5e7eb] space-y-2">
        <div className="text-[11.5px] uppercase tracking-wider text-[#6b7280] font-semibold">Seats</div>
        <div className="text-[12px] text-[#374151]">
          Included: <b>{license.included_user_seats || 5}</b> ·
          Additional: <b data-testid="license-additional-seats">{license.additional_user_seats || 0}</b> ·
          Total: <b>{(license.included_user_seats || 5) + (license.additional_user_seats || 0)}</b>
          {license.license_year && <span className="ml-2 text-[11.5px] text-[#6b7280]">License Year {license.license_year}</span>}
        </div>
        <label className="block text-[11.5px] text-[#6b7280]">Additional seats (comp or purchased)</label>
        <Input type="number" min="0" value={editAdditionalSeats}
          onChange={(e) => setEditAdditionalSeats(Math.max(0, parseInt(e.target.value) || 0))}
          className="bg-white border-[#e5e7eb] h-9 text-[13px]" data-testid="license-edit-additional-seats" />
        <label className="flex items-center gap-2 text-[12px] text-[#374151] mt-1">
          <input type="checkbox" checked={seatsComp} onChange={(e) => setSeatsComp(e.target.checked)} data-testid="license-seats-comp" />
          Mark as comped (no charge)
        </label>
        <Button
          disabled={editAdditionalSeats === (license.additional_user_seats || 0) && !seatsComp}
          onClick={() => onUpdateSeats && onUpdateSeats({ additional_user_seats: editAdditionalSeats, comp: seatsComp })}
          size="sm" className="bg-[#3B4FE4] hover:bg-[#2d3fb8] text-white disabled:opacity-50"
          data-testid="license-save-seats">
          Save Seats
        </Button>
      </div>

      {/* Edit form */}
      <div className="pt-3 border-t border-[#e5e7eb] space-y-2">
        <div className="text-[11.5px] uppercase tracking-wider text-[#6b7280] font-semibold">Edit License</div>
        <label className="block text-[11.5px] text-[#6b7280]">End date</label>
        <Input type="date" value={editEnd} onChange={(e) => setEditEnd(e.target.value)} className="bg-white border-[#e5e7eb] h-9 text-[13px]" data-testid="license-edit-end" />

        <label className="block text-[11.5px] text-[#6b7280]">Annual price (USD)</label>
        <Input type="number" value={editPrice} onChange={(e) => setEditPrice(e.target.value)} className="bg-white border-[#e5e7eb] h-9 text-[13px]" data-testid="license-edit-price" />

        <label className="block text-[11.5px] text-[#6b7280]">Payment method</label>
        <select value={editPayment} onChange={(e) => setEditPayment(e.target.value)} className="border border-[#e5e7eb] bg-white text-[#111827] text-[13px] rounded-md px-2 py-1.5 w-full" data-testid="license-edit-payment">
          <option value="po">Purchase Order</option>
          <option value="cc">Credit Card</option>
          <option value="ach">ACH</option>
          <option value="manual">Manual / Comp</option>
        </select>

        <label className="flex items-center gap-2 text-[12px] text-[#374151] mt-1">
          <input type="checkbox" checked={editAutoRenew} onChange={(e) => setEditAutoRenew(e.target.checked)} data-testid="license-auto-renew" />
          Auto-renew enabled
        </label>

        <Input value={editNotes} onChange={(e) => setEditNotes(e.target.value)} placeholder="Internal notes…" className="bg-white border-[#e5e7eb] h-9 text-[13px]" data-testid="license-edit-notes" />

        <div className="flex items-center justify-between pt-2">
          <Button
            onClick={() => onCancel()}
            variant="outline"
            size="sm"
            className="border-[#fecaca] text-[#b91c1c] hover:bg-[#fef2f2]"
            data-testid="license-cancel-btn"
          >
            Cancel License
          </Button>
          <Button
            disabled={!dirty}
            onClick={() => onUpdate({
              end_date: editEnd ? `${editEnd}T00:00:00+00:00` : null,
              price: Number(editPrice),
              payment_method: editPayment,
              auto_renew_enabled: editAutoRenew,
              notes: editNotes,
            })}
            size="sm"
            className="bg-[#3B4FE4] hover:bg-[#2d3fb8] text-white disabled:opacity-50"
            data-testid="license-save-btn"
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LicensesAdminPage;
